import { autorun, makeAutoObservable, runInAction } from "mobx";
import { AvatarStateStorage } from "../persistent-storage/avatarState";
import { Section } from "./types";
import { Step } from "../steps/v2/step";
import { createAvatarActor, createAvatarStream } from "./avatarManager.fsm";
import { isSafari } from "@/lib/browser";
import { AvatarEventContext, AvatarManagerEvents, AvatarNamespace } from "./avatarManager.events";
import { EventEmitter } from "@/lib/events/eventEmitter";
import invariant from "ts-invariant";

export type SectionMedia = {
  sectionId: string;
  mediaUrl: string;
  mediaDuration: number;
};

export type AvatarMediaError = {
  mediaSource: "current" | "next";
  code: number;
  message: string;
};

type SectionId = string;
type ObjectUrl = string;

export type SectionMediaUrls = Record<SectionId, string>;
export type MediaCache = Record<SectionId, ObjectUrl>;

export interface AvatarManagerParams {
  sectionMedias: SectionMedia[];
  sytemFlowMediaUrls: SectionMediaUrls;
  skipBlobMediaPreload?: boolean;
  playbackRate?: number;
  stateStorage: AvatarStateStorage;
  mediaAmplificationRate?: number;
  highlightPlayButtonEnabled?: boolean;
  showWatermark?: boolean;
  showFeedbackButton?: boolean;
  isCaptionsEnabledByDefault?: boolean;
}

export class AvatarManager {
  private mediaElement: HTMLMediaElement | null = null;

  private audioContext: AudioContext | null = null;
  private mediaSourceNodes: Map<HTMLMediaElement, MediaElementAudioSourceNode> = new Map();
  private gainNode: GainNode | null = null;

  currentTime: number = 0;
  passedTimeOffset: number = 0;
  totalMediaDuration: number = 0;
  playbackRate: number;
  mediaAmplificationRate?: number;

  isMuted: boolean = false;
  isProgressVisible: boolean = true;
  isCaptionsVisible: boolean = true;
  isInitializing: boolean = true;
  isBuffering: boolean = false;
  initializedAt: number = 0;

  currentSection?: Section;
  currentStep?: Step;

  private stateStorage: AvatarStateStorage;
  private sectionMedias: SectionMedia[] = [];
  private sytemFlowMediaUrls: SectionMediaUrls = {};
  private mediaCache: MediaCache = {};
  private skipBlobMediaPreload?: boolean;
  private mediaTimeTrackerStopFn?: () => void;
  private isSystemMediaPreloaded = false;
  private highlightPlayButtonEnabled = false;
  private showWatermark = false;
  private showFeedbackButton = true;

  private bufferingCheckIntervalId: ReturnType<typeof setInterval> | null = null;
  private readonly bufferingCheckInterval = 200;
  private bufferingCheckState = {
    lastPlayPos: 0,
  };

  events = new EventEmitter<typeof AvatarNamespace, AvatarManagerEvents, AvatarEventContext>(AvatarNamespace);

  machine: ReturnType<typeof createAvatarActor>;
  machineStream: ReturnType<typeof createAvatarStream>;

  constructor({
    sectionMedias,
    stateStorage,
    sytemFlowMediaUrls: systemFlowMedias,
    skipBlobMediaPreload,
    playbackRate,
    mediaAmplificationRate,
    highlightPlayButtonEnabled,
    showWatermark,
    isCaptionsEnabledByDefault,
    showFeedbackButton,
  }: AvatarManagerParams) {
    makeAutoObservable(this);
    this.stateStorage = stateStorage;
    this.sectionMedias = sectionMedias;
    this.sytemFlowMediaUrls = systemFlowMedias;
    this.totalMediaDuration = sectionMedias.reduce((acc, section) => acc + section.mediaDuration, 0);
    this.skipBlobMediaPreload = skipBlobMediaPreload;
    this.playbackRate = playbackRate ?? 1;
    this.mediaAmplificationRate = mediaAmplificationRate;
    this.highlightPlayButtonEnabled = highlightPlayButtonEnabled ?? false;
    this.showWatermark = showWatermark ?? false;
    this.showFeedbackButton = showFeedbackButton ?? true;
    this.initializedAt = Date.now();

    const initialState = this.stateStorage.get();
    this.isCaptionsVisible = initialState.isCaptionsVisible ?? isCaptionsEnabledByDefault ?? true;
    this.isMuted = initialState.isMuted;

    if (this.shouldAmplifyMedia) {
      this.setupMediaAmplification();
    }

    autorun(() => {
      this.stateStorage.set({
        isCaptionsVisible: this.isCaptionsVisible,
        isMuted: this.isMuted,
      });
    });

    this.machine = createAvatarActor({
      hooks: {
        onPlayMedia: this.playMedia.bind(this),
        onPauseMedia: this.pauseMedia.bind(this),
        onAutoPlayMedia: this.tryAutoPlayMedia.bind(this),
        onReplayStep: this.handleReplayStep.bind(this),
        onSkipStep: this.handleSkipStep.bind(this),
        onFinishMedia: this.handleFinishMedia.bind(this),
        onSyncMediaElementCurrentTime: this.handleSyncMediaElementCurrentTime.bind(this),
      },
    });
    this.machineStream = createAvatarStream(this.machine);
    this.machine.start();
  }

  setCurrentSection(
    section: Section,
    step: Step,
    options: { currentTime?: number; autoPlay: boolean; isProgressRingVisible: boolean }
  ) {
    this.currentSection = section;
    this.currentStep = step;
    this.isProgressVisible = options.isProgressRingVisible;

    const currentTime = options?.currentTime ?? 0;
    this.setCurrentTime(currentTime);
    this.bufferingCheckState.lastPlayPos = currentTime;

    this.updatePassedTimeOffset(section.id);

    this.machine.send({ type: "sectionTransition", section, step, playSettings: { autoPlay: options.autoPlay } });

    this.initMediaTimeTracker();
  }

  setCurrentStep(step: Step) {
    this.machine.send({ type: "stepTransition", step });
  }

  onMediaElementChanged(el: HTMLMediaElement) {
    this.mediaElement = el;

    this.setupBufferingCheck();

    this.mediaElement.playbackRate = this.playbackRate;
    if (this.shouldAmplifyMedia) {
      this.amplifyMedia(this.mediaElement);
    }
    this.machine.send({ type: "mediaElementInitialized" });
  }

  setupBufferingCheck() {
    if (this.bufferingCheckIntervalId) clearInterval(this.bufferingCheckIntervalId);
    this.bufferingCheckIntervalId = setInterval(this.isMediaBuffering.bind(this), this.bufferingCheckInterval);
  }

  handleReplayStep(step: Step) {
    this.events.emit("stepReplayed", step);
  }

  handleSkipStep(step: Step) {
    this.events.emit("stepSkipped", step);
  }

  handleSyncMediaElementCurrentTime(time: number) {
    if (!this.mediaElement) return;
    if (time === this.mediaElement.currentTime * 1000) return;
    this.seekMedia(time);
  }

  play() {
    this.machine.send({ type: "systemPlay" });
  }

  private playMedia() {
    this.mediaElement?.play();
    this.events.emit("mediaPlayTriggered", {}, this.buildEventContext());
  }

  async tryAutoPlayMedia() {
    await this.mediaElement?.play();
    this.events.emit("mediaPlayTriggered", {}, this.buildEventContext());
  }

  pause() {
    this.machine.send({ type: "systemPause" });
  }

  private pauseMedia() {
    this.mediaElement?.pause();
    this.events.emit("mediaPauseTriggered", {}, this.buildEventContext());
  }

  onPlayClicked() {
    this.events.emit("playClicked", {}, this.buildEventContext());
    this.machine.send({ type: "playClicked" });
  }

  onPauseClicked() {
    this.events.emit("pauseClicked", {}, this.buildEventContext());
    this.machine.send({ type: "pauseClicked" });
  }

  onReplayClicked() {
    this.events.emit("replayClicked", {}, this.buildEventContext());
    this.machine.send({ type: "replayClicked" });
  }

  onSkipClicked() {
    this.events.emit("skipClicked", {}, this.buildEventContext());
    this.machine.send({ type: "skipClicked" });
  }

  get isControlsAlwaysVisible() {
    return this.isAutoPlayFailed || this.isPausedForDisabledAutoPlay;
  }

  get cantPlay() {
    return (
      this.isAutoPlayFailed ||
      this.isPausedForDisabledAutoPlay ||
      (this.currentStep?.isAtom && this.currentStep?.isBlocking)
    );
  }

  get isAutoPlayFailed() {
    return this.state!.hasTag("autoPlayFailed");
  }

  get canPlay() {
    return this.state!.can({ type: "playClicked" });
  }

  get canPause() {
    return this.state!.can({ type: "pauseClicked" });
  }

  get canSkip() {
    return this.state!.can({ type: "skipClicked" });
  }

  get canReplay() {
    return this.state!.can({ type: "replayClicked" });
  }

  get isMinimized() {
    return this.state?.hasTag("minimized");
  }

  get isPlaying() {
    return this.state?.matches({ running: "playing" });
  }

  get progress() {
    if (this.totalMediaDuration === 0) return 0;
    if (!this.context) return 0;

    return ((this.passedTimeOffset + this.currentTime) / this.totalMediaDuration) * 100;
  }

  get currentMediaDuration() {
    return this.context?.section?.mediaDuration ?? 0;
  }

  onMediaPlay() {
    this.machine.send({ type: "mediaPlaying" });
    this.events.emit("mediaPlayed", {}, this.buildEventContext());
  }

  onMediaCanPlay() {
    this.isInitializing = false;
    this.machine.send({ type: "mediaCanPlay" });
  }

  onMediaError(error: AvatarMediaError) {
    this.events.emit("mediaError", error, this.buildEventContext());
  }

  onMediaCanPlayThrough() {
    if (this.isSystemMediaPreloaded) return;
    this.isSystemMediaPreloaded = true;
    this.preloadSystemFlowMedia();
  }

  onMediaPaused() {
    this.machine.send({ type: "mediaPaused" });
    this.events.emit("mediaPaused", {}, this.buildEventContext());
  }

  handleFinishMedia() {
    this.events.emit("mediaFinished", {}, this.buildEventContext());
  }

  onMediaFinished() {
    this.setCurrentTime(this.currentMediaDuration);
    this.machine.send({ type: "mediaFinished" });
    this.events.emit("mediaFinished", {}, this.buildEventContext());
  }

  onMuteChanged(isMuted: boolean) {
    this.isMuted = isMuted;
    this.events.emit("muteClicked", { status: isMuted }, this.buildEventContext());
  }

  onShowCaptionsChanged(shouldShowCaptions: boolean) {
    this.isCaptionsVisible = shouldShowCaptions;
    this.events.emit("captionClicked", { status: shouldShowCaptions }, this.buildEventContext());
  }

  onMinimizeClicked() {
    this.events.emit("minimizeClicked", {}, this.buildEventContext());
  }

  onExpandClicked() {
    this.events.emit("expandClicked", {}, this.buildEventContext());
  }

  onDismissClicked() {
    this.machine.send({ type: "dismissClicked" });
    this.events.emit("dismissClicked", {}, this.buildEventContext());
  }

  minimize() {
    this.machine.send({ type: "minimize" });
  }

  expand() {
    this.machine.send({ type: "expand" });
  }

  terminate() {
    this.machine.send({ type: "terminate" });
    this.mediaTimeTrackerStopFn?.();
    if (this.bufferingCheckIntervalId) clearInterval(this.bufferingCheckIntervalId);
    this.audioContext?.close();
  }

  get status() {
    return this.machineStream.current?.value;
  }

  updatePassedTimeOffset(sectionId: string) {
    const section = this.sectionMedias.find((s) => s.sectionId === sectionId);
    if (!section) return;
    const sectionIndex = this.sectionMedias.indexOf(section);
    const passedTime = this.sectionMedias.slice(0, sectionIndex).reduce((acc, s) => acc + s.mediaDuration, 0);
    this.passedTimeOffset = passedTime;
  }

  seekMediaToEnd() {
    this.seekMedia(this.currentMediaDuration);
  }

  seekMedia(to: number) {
    runInAction(() => {
      if (this.mediaElement) {
        this.pause();

        if (isSafari) {
          this.mediaElement.load(); // Required for Safari
        }
        this.mediaElement.currentTime = to / 1000;
        this.setCurrentTime(to);
        this.events.emit("mediaTimeUpdated", to, this.buildEventContext(), { skipGlobal: true });
        this.events.emit("mediaSeeked", { seekedTo: to }, this.buildEventContext(), { skipGlobal: true });
      }
    });
  }

  get nextSectionMediaUrl() {
    if (!this.currentSection) return "";

    const currentSectionIndex = this.currentSection.index;
    const nextSection = this.sectionMedias.find(
      (s) => s.sectionId === this.sectionMedias[currentSectionIndex + 1]?.sectionId
    );
    return nextSection?.mediaUrl;
  }

  get originalMediaUrl() {
    if (!this.currentSection) return "";
    return this.currentSection.avatarMediaUrl;
  }

  get mediaUrl(): string {
    if (!this.currentSection) return "";

    if (this.mediaCache[this.currentSection.id]) {
      return this.mediaCache[this.currentSection.id];
    }

    return this.currentSection.avatarMediaUrl;
  }

  get state() {
    return this.machineStream.current;
  }

  get context() {
    return this.machineStream.current?.context;
  }

  get isPausedForDisabledAutoPlay() {
    return this.state?.hasTag("pausedForDisabledAutoPlay");
  }

  get shouldHighlightPlayButton() {
    return this.highlightPlayButtonEnabled && this.isPausedForDisabledAutoPlay;
  }

  get shouldShowWatermark() {
    return this.showWatermark;
  }

  get shouldShowFeedbackButton() {
    return this.showFeedbackButton;
  }

  toggleInitializing() {
    this.isInitializing = !this.isInitializing;
  }

  private setCurrentTime(time: number) {
    runInAction(() => {
      this.currentTime = time;
      this.events.emit("mediaTimeUpdated", time, this.buildEventContext(), { skipGlobal: true });
      this.machine.send({ type: "timeUpdate", currentTime: time });
    });
  }

  private initMediaTimeTracker() {
    this.mediaTimeTrackerStopFn?.();
    this.mediaTimeTrackerStopFn = undefined;

    this.mediaTimeTrackerStopFn = autorun(() => {
      const updateTime = () => {
        if (!this.mediaElement || !this.isPlaying) return;
        this.setCurrentTime(this.mediaElement.currentTime * 1000);
        requestAnimationFrame(updateTime.bind(this));
      };

      if (this.isPlaying) {
        updateTime();
      }
    });

    this.events.emit("mediaTrackerInitialized", {}, this.buildEventContext());
  }

  private preloadSystemFlowMedia() {
    if (this.skipBlobMediaPreload) {
      return;
    }

    Object.entries(this.sytemFlowMediaUrls).forEach(([sectionId, mediaUrl]) => {
      const isMediaAlreadyServed = this.mediaUrl === mediaUrl;
      if (isMediaAlreadyServed) {
        return;
      }

      if (mediaUrl.length > 0 && mediaUrl.indexOf("https") === 0) {
        fetch(mediaUrl)
          .then((res) => res.blob())
          .then((blob) => {
            this.mediaCache[sectionId] = URL.createObjectURL(blob);
          })
          .catch((err) => {
            console.error(err);
          });
      }
    });
  }

  get isPaused() {
    return this.state?.matches({ running: "paused" });
  }

  private isMediaBuffering() {
    if (!this.mediaElement) return;

    const currentPlayPos = this.currentTime;

    // Offset is used to provide a small tolerance for playback position changes
    // It allows for minor fluctuations in playback without triggering buffering detection
    const offset = (this.bufferingCheckInterval - 20) / 1000;

    const isPlaybackPositionAdvanced = currentPlayPos > this.bufferingCheckState.lastPlayPos + offset;
    const isPlaybackPositionStalled = currentPlayPos <= this.bufferingCheckState.lastPlayPos + offset;
    const isMediaNotReadyToPlay = this.mediaElement.readyState < 2;

    if ((!this.isBuffering && isPlaybackPositionStalled && this.isPlaying) || isMediaNotReadyToPlay) {
      this.isBuffering = true;
    }

    if ((this.isBuffering && isPlaybackPositionAdvanced && this.isPlaying) || this.cantPlay || this.isPaused) {
      this.isBuffering = false;
    }

    this.bufferingCheckState.lastPlayPos = currentPlayPos;
  }

  get shouldAmplifyMedia() {
    return !!this.mediaAmplificationRate && !isSafari;
  }

  private setupMediaAmplification() {
    invariant(!!this.mediaAmplificationRate, "mediaAmplificationRate is not set");

    this.audioContext = new AudioContext();
    this.gainNode = this.audioContext.createGain();
    this.gainNode.gain.value = this.mediaAmplificationRate!;
    this.gainNode!.connect(this.audioContext.destination);
  }

  private amplifyMedia(mediaElement: HTMLMediaElement) {
    invariant(!!this.audioContext, "audioContext is not initialized");
    invariant(!!this.gainNode, "gainNode is not initialized");

    if (!this.mediaSourceNodes.has(mediaElement)) {
      const mediaSourceNode = this.audioContext!.createMediaElementSource(mediaElement);
      mediaSourceNode.connect(this.gainNode!);
      this.mediaSourceNodes.set(mediaElement, mediaSourceNode);
    }
  }

  private buildEventContext(): AvatarEventContext {
    return {
      time: this.currentTime,
      sectionIndex: this.currentSection?.index ?? -1,
      avatarStatus: this.state?.value ? JSON.stringify(this.state.value) : "",
    };
  }
}
